import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Button } from 'src/components/system/Button';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
};

type NavigatorWithStandalone = Navigator & {
  standalone?: boolean;
  maxTouchPoints?: number;
};

interface PwaInstallBannerProps {
  isMobile: boolean;
}

const Banner = styled.section`
  width: 100%;
  border-radius: 14px;
  padding: 14px;
  margin: 0 0 14px;
  border: 1px solid rgba(15, 123, 107, 0.28);
  background: linear-gradient(128deg, rgba(220, 255, 251, 0.88), rgba(234, 246, 255, 0.9));
  box-shadow: 0 12px 24px rgba(15, 42, 66, 0.08);
  display: flex;
  flex-direction: column;
  gap: 10px;

  strong {
    font-size: 0.95rem;
    color: var(--blue-700);
  }

  p {
    margin: 0;
    color: var(--gray-600);
    font-size: 0.84rem;
    line-height: 1.35;
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export function PwaInstallBanner({ isMobile }: PwaInstallBannerProps) {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [sharing, setSharing] = useState(false);

  const isIOS = useMemo(() => {
    if (typeof navigator === 'undefined') {
      return false;
    }

    const nav = navigator as NavigatorWithStandalone;
    const ua = navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(ua);
    const iPadOSDesktopMode = navigator.platform === 'MacIntel' && (nav.maxTouchPoints ?? 0) > 1;

    return isIOSDevice || iPadOSDesktopMode;
  }, []);

  const isDeviceMobile = useMemo(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return false;
    }

    const ua = navigator.userAgent.toLowerCase();
    const hasMobileUA = /android|iphone|ipad|ipod|mobile/.test(ua);
    const supportsMatchMedia = typeof window.matchMedia === 'function';
    const coarsePointer = supportsMatchMedia ? window.matchMedia('(pointer: coarse)').matches : false;
    const narrowViewport = supportsMatchMedia ? window.matchMedia('(max-width: 920px)').matches : false;

    return hasMobileUA || coarsePointer || narrowViewport;
  }, []);

  const supportsNativeShare = useMemo(() => {
    if (typeof navigator === 'undefined') {
      return false;
    }

    return typeof navigator.share === 'function';
  }, []);

  useEffect(() => {
    const updateStandaloneState = () => {
      const nav = window.navigator as NavigatorWithStandalone;
      const byDisplayMode = typeof window.matchMedia === 'function'
        ? window.matchMedia('(display-mode: standalone)').matches
        : false;
      const byNavigator = nav.standalone === true;
      setIsStandalone(byDisplayMode || byNavigator);
    };

    updateStandaloneState();
    window.addEventListener('appinstalled', updateStandaloneState);

    return () => {
      window.removeEventListener('appinstalled', updateStandaloneState);
    };
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setDismissed(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const canPromptInstall = deferredPrompt !== null;
  const canOpenShareShortcut = isIOS && !canPromptInstall && supportsNativeShare;
  const shouldShow = (isMobile || isDeviceMobile)
    && !isStandalone
    && !dismissed
    && (canPromptInstall || isIOS);

  async function handleInstall() {
    if (!deferredPrompt || installing) {
      return;
    }

    setInstalling(true);

    try {
      await deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } finally {
      setDeferredPrompt(null);
      setInstalling(false);
    }
  }

  async function handleOpenShareShortcut() {
    if (!canOpenShareShortcut || sharing) {
      return;
    }

    setSharing(true);

    try {
      await navigator.share({
        title: 'ART - Simulatoro',
        text: 'No iPhone/iPad, toque em "Adicionar à Tela de Início" para instalar o app.',
        url: window.location.href,
      });
    } catch {
      // User can cancel the share sheet. No-op.
    } finally {
      setSharing(false);
    }
  }

  if (!shouldShow) {
    return null;
  }

  return (
    <Banner>
      <strong>Instale o app no celular</strong>
      {canPromptInstall ? (
        <p>
          Instale o Simulatoro para abrir em tela cheia, com acesso rápido e experiência de app.
        </p>
      ) : (
        <p>
          No iPhone/iPad, atalho rápido: toque em Compartilhar e depois em &quot;Adicionar à Tela de Início&quot;.
        </p>
      )}
      <ActionRow>
        {canPromptInstall && (
          <Button
            type="button"
            width="150px"
            height="36px"
            backgroundColor="#0f7b6b"
            borderColor="#0f7b6b"
            buttonColor="#ffffff"
            disabled={installing}
            onClick={handleInstall}
            label={<span>{installing ? 'Instalando...' : 'Instalar app'}</span>}
          />
        )}
        {canOpenShareShortcut && (
          <Button
            type="button"
            width="170px"
            height="36px"
            backgroundColor="#0f7b6b"
            borderColor="#0f7b6b"
            buttonColor="#ffffff"
            disabled={sharing}
            onClick={handleOpenShareShortcut}
            label={<span>{sharing ? 'Abrindo...' : 'Abrir Compartilhar'}</span>}
          />
        )}
        <Button
          type="button"
          width="120px"
          height="36px"
          backgroundColor="#ffffff"
          borderColor="#1e4f79"
          buttonColor="#1e4f79"
          onClick={() => setDismissed(true)}
          label={<span>Agora não</span>}
        />
      </ActionRow>
    </Banner>
  );
}
